<template>
  <component
    :is="wrapperComponent"
    v-bind="$attrs"
    :data-kpb-caption-placement="captionPlacement"
    :data-kpb-caption-visibility="captionVisibility"
    class="kpb-photoswipe"
    :id="wrapperId"
  >
    <a
      v-for="(item, key) in data"
      class="kpb-photoswipe__item"
      :class="childClass"
      :key="key"
      :href="item.url"
      :data-pswp-type="!['vimeo', 'youtube'].includes(item.type) ? 'image' : item.type"
      :data-pswp-width="$path('dimensions.0', item)"
      :data-pswp-height="$path('dimensions.1', item)"
      :data-pswp-caption="item.description"
      target="_blank"
      rel="noreferrer"
      data-cropped="true"
    >
      <slot name="default">
        <Media v-if="['vimeo', 'youtube'].includes(item.type)" :data="item" :type="item.type" />
        <img
          v-else
          :src="item.url"
          :alt="item.description"
          class="kpb-photoswipe__item-thumbnail"
          :class="`${childClass}-thumbnail`"
        />

        <span
          v-if="item.description"
          class="kpb-photoswipe__item-caption-wrapper"
          :class="`${childClass}-caption-wrapper`"
        >
          <span class="kpb-photoswipe__item-caption" :class="`${childClass}-caption`"> {{ item.description }}</span>
        </span>
      </slot>
    </a>
  </component>
</template>

<script>
import Media from './Media.vue'
import { LoadScript } from '../../utils'

/** PhotoSwipe */
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js'
import 'photoswipe/dist/photoswipe.css'
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin'
import 'photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.css'

function ChangeVideoState(value, state = 'ready') {
  if (!value.data || !['vimeo', 'youtube'].includes(value.data.type)) return

  const element = document.querySelector(`.pswp__video-container--${value.index} kvass-${value.data.type}`)
  if (element) element.setAttribute('video-state', state)
}

/** Gallery component */
export default {
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    wrapper: {
      type: Object,
    },
    wrapperId: {
      type: String,
      default: `kpb-photoswipe-${Math.random().toString(36).slice(2, 9)}`,
    },
    controls: {
      type: Array,
      default: () => ['print', 'download'],
    },
    videoMute: {
      type: Boolean,
      default: false,
    },

    videoAutoplay: {
      type: Boolean,
      default: false,
    },
    captionPosition: {
      type: String,
      default: 'auto',
      enums: ['auto', 'below', 'aside'],
    },
    captionVisibility: {
      type: String,
      default: 'visible',
      enums: ['hover', 'visible', 'hidden'],
    },
    captionPlacement: {
      type: String,
      default: 'inside',
      enums: ['inside', 'outside', 'center'],
    },
    childClass: {
      type: String,
    },
  },
  data() {
    return {
      lightbox: null,
      dynamicCaption: null,
      currentSlide: null,
    }
  },
  computed: {
    wrapperComponent() {
      return this.wrapper || 'div'
    },
  },
  mounted() {
    LoadScript('https://unpkg.com/@kvass/widgets@latest/dist/vimeo.js', { type: 'module' })
    LoadScript('https://unpkg.com/@kvass/widgets@latest/dist/youtube.js', { type: 'module' })

    if (!this.lightbox) {
      this.lightbox = new PhotoSwipeLightbox({
        gallery: '#' + this.wrapperId,
        children: 'a',
        pswpModule: () => import('photoswipe'),

        // Use different padding based on viewport width
        paddingFn: ({ x: viewportWidth }) => {
          if (viewportWidth <= 600) return { top: 0, bottom: 0, left: 0, right: 0 }

          return {
            top: 32,
            bottom: 32,
            left: 64,
            right: 64,
          }
        },

        // Translations
        errorMsg: 'Bildet kan ikke lastes',
        indexIndicatorSep: ' av ',
        closeTitle: 'Lukk',
        zoomTitle: 'Zoom',
        arrowPrevTitle: 'Forrige',
        arrowNextTitle: 'Neste',
      })

      this.dynamicCaption = new PhotoSwipeDynamicCaption(this.lightbox, {
        type: this.captionPosition || 'auto',
        captionContent: slide => {
          if (!slide.data.element.dataset) return
          return slide.data.element.dataset.pswpCaption
        },
      })

      let UIelements = [
        {
          condition: this.controls.includes('download'),
          content: {
            name: 'download-button',
            order: 8,
            isButton: true,
            tagName: 'a',
            html: {
              isCustomSVG: true,
              inner: '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z"/>',
              outlineID: 'pswp__icon-download',
            },
            onInit: (el, pswp) => {
              el.setAttribute('download', '')
              el.setAttribute('target', '_blank')
              el.setAttribute('rel', 'noopener')

              pswp.on('change', () => {
                el.href = pswp.currSlide.data.src
              })
            },
          },
        },
        {
          condition: this.controls.includes('print'),
          content: {
            name: 'print-button',
            order: 9,
            isButton: true,
            tagName: 'button',
            html: {
              isCustomSVG: true,
              inner:
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M448 192V77.25c0-8.49-3.37-16.62-9.37-22.63L393.37 9.37c-6-6-14.14-9.37-22.63-9.37H96C78.33 0 64 14.33 64 32v160c-35.35 0-64 28.65-64 64v112c0 8.84 7.16 16 16 16h48v96c0 17.67 14.33 32 32 32h320c17.67 0 32-14.33 32-32v-96h48c8.84 0 16-7.16 16-16V256c0-35.35-28.65-64-64-64zm-64 256H128v-96h256v96zm0-224H128V64h192v48c0 8.84 7.16 16 16 16h48v96zm48 72c-13.25 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.74 24 24c0 13.25-10.75 24-24 24z"/></svg> ',
              outlineID: 'pswp__icon-print',
            },
            onInit: (el, pswp) => {
              let currentImage
              function print() {
                let print = window.open('')
                print.document.write(
                  `<html><head></head><body><img style='display:block;margin-left:auto;margin-right:auto;width:100%;height:auto;'src=${currentImage.src}></img></body></html>`,
                )
                print.document.close()
                setTimeout(() => {
                  print.print()
                  print.close()
                }, 500)
              }
              el.onclick = function () {
                print()
              }
              pswp.on('change', () => {
                currentImage = pswp.currSlide.data
              })
            },
          },
        },
      ].filter(f => !('condition' in f) || f.condition)

      if (UIelements.length) {
        this.lightbox.on('uiRegister', () => {
          UIelements.forEach(i => {
            this.lightbox.pswp.ui.registerElement(i.content)
          })
        })
      }

      this.lightbox.on('beforeOpen', () => {
        this.$emit('stop')
      })

      this.lightbox.on('close', () => {
        this.$emit('start', this.currentSlide)
      })
      // override slide content if vimeo or youtube type
      this.lightbox.on('contentLoad', e => {
        const { content } = e

        if (['vimeo', 'youtube'].includes(content.type)) {
          // prevent the deafult behavior
          e.preventDefault()

          content.element = document.createElement('div')
          content.element.classList.add('pswp__video-container', `pswp__video-container--${content.index}`)

          const kvassVideoEmbed = document.createElement(`kvass-${content.type}`)
          kvassVideoEmbed.setAttribute('url', content.data.src)
          kvassVideoEmbed.setAttribute('video-state', 'ready')

          content.element.appendChild(kvassVideoEmbed)
        }
      })

      this.lightbox.on('change', () => {
        //change video state to prevent playing in the background on change
        let current = this.$path('pswp.currSlide', this.lightbox)

        if (current) ChangeVideoState(current, 'ready')
        if (this.currentSlide) ChangeVideoState(this.currentSlide, 'pause')

        this.currentSlide = current
      })

      this.lightbox.init()
    }
  },
  unmounted() {
    if (this.lightbox) {
      this.lightbox.destroy()
      this.lightbox = null
    }
  },
  components: {
    Media,
  },
}
</script>

<style lang="scss">
.kpb-photoswipe {
  height: inherit;

  iframe {
    pointer-events: none;
  }
  &__item {
    height: inherit;
    position: relative;
    display: flex;
    flex-direction: column;
    text-decoration: none;
    justify-content: center;
    background-color: #eaeaea;

    &-thumbnail {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    [data-kpb-caption-visibility='hover'] & {
      .kpb-photoswipe__item-caption {
        opacity: 0;
      }

      &:hover {
        .kpb-photoswipe__item-caption {
          opacity: 1;
        }
      }
    }

    &-caption {
      &-wrapper {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 0.5em 0.75em;
        text-shadow: 0 2px 4px rgba(black, 0.75);
        background: linear-gradient(to bottom, rgba(black, 0), rgba(black, 0.75));
        color: white;
        transition: opacity 200ms ease-out;
        pointer-events: none;
        word-break: break-word;

        @include respond-below('phone') {
          font-size: 0.7em;
        }

        [data-kpb-caption-placement='inside'] & {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        [data-kpb-caption-placement='outside'] & {
          position: initial;
          padding: 0.5em 0;
          text-shadow: none;
          background: none;
          color: inherit;
        }

        [data-kpb-caption-placement='center'] & {
          top: 50%;
          left: 50%;
          translate: -50% -50%;
          text-align: center;
          bottom: unset;
          background: rgba(0, 0, 0, 0.75);
          right: unset;
          max-height: 100%;
          overflow: hidden;
        }

        [data-kpb-caption-visibility='hidden'] &,
        [data-kpb-caption-placement='none'] & {
          display: none;
        }
      }
    }
  }

  .kvass-media-render {
    &-youtube__blocked,
    &-vimeo__blocked {
      transform: translate(-50%, -50%) !important;
    }
  }
}
.pswp {
  img {
    max-width: none;
    object-fit: contain;
  }

  &__button--print-button {
    .pswp__icn {
      $size: 18px;
      max-height: $size;
      top: $size + 4px;
    }
  }

  &__video-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
